import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import { useGetPASupplier } from "@/hooks/PAHooks";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { usePermissions } from "@/hooks/usePermissions";
import type { PASupplier as PaSupplierType } from "@/api/PAService";
import { formattedDateTime } from "@/utils/date";
import {
  DefaultPrimaryButton,
} from "@/styles/globalstyles";
import SelectBuyerCodeModal from "./SelectBuyerCodeModal";

const needsBuyerCodeSelection = (buyerCode: string | null) =>
  (buyerCode?.split(",").filter((c) => c.trim()).length ?? 0) > 1;

const PASupplier = () => {
  const { canView, canEdit } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingSupplier, setEditingSupplier] = useState<PaSupplierType | null>(null);

  // Set default result to waiting on first load
  useEffect(() => {
    if (!searchParams.get("result")) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("result", "Wait");
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page = useMemo(
    () => Number(searchParams.get("page")) || 0,
    [searchParams],
  );
  const rowsPerPage = useMemo(
    () => Number(searchParams.get("limit")) || 10,
    [searchParams],
  );
  const searchFilter = searchParams.get("search") || "";
  const buyerCode = searchParams.get("buyer_code") || "";
  const result = (searchParams.get("result") || "") as
    | ""
    | "Wait"
    | "success";
  const needsSelection = searchParams.get("needs_selection") === "1";

  // Draft filter state — only applied to the URL (and thus the query) when
  // the user clicks "ค้นหา" (or presses Enter), instead of on every keystroke.
  const [draftSearch, setDraftSearch] = useState(searchFilter);
  const [draftBuyerCode, setDraftBuyerCode] = useState(buyerCode);
  const [draftResult, setDraftResult] = useState(result);
  const [draftNeedsSelection, setDraftNeedsSelection] = useState(needsSelection);

  useEffect(() => {
    setDraftSearch(searchFilter);
    setDraftBuyerCode(buyerCode);
    setDraftResult(result);
    setDraftNeedsSelection(needsSelection);
  }, [searchFilter, buyerCode, result, needsSelection]);

  // Bumped on every "ค้นหา" click so identical filters still force a refetch.
  const [searchTrigger, setSearchTrigger] = useState(0);

  const { data, isFetching, isError, error, refetch } = useGetPASupplier({
    search_filter: searchFilter || undefined,
    buyer_code: buyerCode || undefined,
    result: result || undefined,
    needs_buyer_code_selection: needsSelection || undefined,
    skip: page * rowsPerPage,
    limit: rowsPerPage,
  });

  useEffect(() => {
    if (searchTrigger > 0) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTrigger]);

  useQueryHandler({
    isError,
    error,
    errorTitle: "โหลดข้อมูลไม่สำเร็จ",
  });

  useEffect(() => {
    document.title = "PA Supplier | AMPA";
  }, []);

  const handleSearch = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "0");
      if (draftSearch) next.set("search", draftSearch);
      else next.delete("search");
      if (draftBuyerCode) next.set("buyer_code", draftBuyerCode);
      else next.delete("buyer_code");
      if (draftResult) next.set("result", draftResult);
      else next.delete("result");
      if (draftNeedsSelection) next.set("needs_selection", "1");
      else next.delete("needs_selection");
      return next;
    }, { replace: true });
    setSearchTrigger((c) => c + 1);
  };

  const columns: GridColDef<PaSupplierType>[] = [
    {
      field: "pa_supplier_id",
      headerName: "PA Supplier ID",
      width: 130,
      headerAlign: "right",
      align: "right",
    },
    { field: "supplier_code", headerName: "Supplier Code", width: 150 },
    {
      field: "buyer_code",
      headerName: "Buyer Code",
      width: 120,
      cellClassName: (params) =>
        needsBuyerCodeSelection(params.value) ? "buyer-code-needs-selection" : "",
    },
    {
      field: "result",
      headerName: "Result",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    { field: "note", headerName: "Note", width: 200, flex: 1 },
    {
      field: "is_admin_reason",
      headerName: "Is Admin Reason",
      width: 130,
      headerAlign: "center",
      align: "center",
      valueFormatter: (v) => (v === true ? "Yes" : v === false ? "No" : "-"),
    },
    {
      field: "create_date",
      headerName: "Create Date",
      width: 160,
      valueFormatter: (v) => (v ? formattedDateTime(v as string) : "-"),
    },
    {
      field: "update_date",
      headerName: "Update Date",
      width: 160,
      valueFormatter: (v) => (v ? formattedDateTime(v as string) : "-"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const needsSelectionRow = needsBuyerCodeSelection(params.row.buyer_code);
        return (
          <Tooltip title="เลือก Buyer Code" placement="top">
            <span>
              <IconButton
                size="small"
                disabled={!canEdit("pa.supplier") || !needsSelectionRow}
                onClick={() => setEditingSupplier(params.row)}
              >
                <BorderColorIcon
                  fontSize="small"
                  color={
                    canEdit("pa.supplier") && needsSelectionRow
                      ? "primary"
                      : "disabled"
                  }
                />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  if (!canView("pa.supplier")) return null;

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Breadcrumb current="pa.supplier" />
      <Box
        sx={{
          mt: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          backgroundColor: "#fafafa",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            label="ค้นหา (Supplier Code, PA Supplier ID)"
            size="small"
            value={draftSearch}
            onChange={(e) => setDraftSearch(e.target.value)}
            sx={{
              width: 280,
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#cccccc",
                },
                "&:hover fieldset": {
                  borderColor: "#999999",
                },
              },
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <TextField
            label="Buyer Code"
            size="small"
            value={draftBuyerCode}
            onChange={(e) => setDraftBuyerCode(e.target.value)}
            sx={{
              width: 150,
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#cccccc",
                },
                "&:hover fieldset": {
                  borderColor: "#999999",
                },
              },
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <FormControl size="small" sx={{ width: 140 }}>
            <InputLabel
              sx={{
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              }}
            >
              Result
            </InputLabel>
            <Select
              value={draftResult}
              label="Result"
              onChange={(e) =>
                setDraftResult(e.target.value as "" | "Wait" | "success")
              }
            >
              <MenuItem value="">ทั้งหมด</MenuItem>
              <MenuItem value="Wait">Wait</MenuItem>
              <MenuItem value="success">success</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={draftNeedsSelection}
                onChange={(e) => setDraftNeedsSelection(e.target.checked)}
              />
            }
            label="ต้องเลือก Buyer Code"
            sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
          />
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            <Button
              size="small"
              startIcon={<SearchIcon />}
              sx={{ ...DefaultPrimaryButton }}
              onClick={handleSearch}
            >
              ค้นหา
            </Button>
          </Box>
        </Box>

        <DataGrid
          rows={data?.data ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isFetching}
          getRowId={(row) => row.pa_supplier_id}
          paginationMode="server"
          paginationModel={{ page, pageSize: rowsPerPage }}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          disableColumnSelector
          disableColumnFilter
          disableColumnMenu
          disableColumnSorting
          sx={{
            minHeight: "50vh",
            maxHeight: "70vh",
            "& .buyer-code-needs-selection": {
              color: "error.main",
              fontWeight: 600,
            },
          }}
          onPaginationModelChange={(model) => {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("page", String(model.page));
              next.set("limit", String(model.pageSize));
              return next;
            }, { replace: true });
          }}
        />
      </Box>

      <SelectBuyerCodeModal
        open={!!editingSupplier}
        handleClose={() => setEditingSupplier(null)}
        supplier={editingSupplier}
      />
    </Box>
  );
};

export default PASupplier;
